/**
 * Connections Page (nav label "Connections" — src/components/layout/AdminLayout.tsx)
 * HubSpot: OAuth connector (src/modules/connectors) — a one-click "Connect"
 * button, not a pasted webhook secret. See admin.api.ts's HubSpot Connector
 * section for the endpoints this page calls.
 *
 * Google Sheets: read-only OAuth connector for spreadsheets — including the
 * sheet Meta writes Lead Ads into, which is how Meta leads reach this product
 * without depending on the Meta pipeline directly.
 *
 * Meta Lead Ads deliberately has no UI of its own here — that pipeline isn't
 * reliable enough to surface yet (see the connector audit in project memory).
 *
 * Layout follows the researched industry pattern (HubSpot's own native Data
 * Sync card, re-checked against this project's own research pass): the
 * PRIMARY surface is a single plain-language state line ("Up to date"),
 * never a run-by-run feed — a detailed history is one deliberate click away
 * (progressive disclosure), not shown by default.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  LinearProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Chip,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTerminology } from '@/hooks/useTerminology';
import {
  CheckCircle,
  ErrorOutline,
  Sync,
  LinkOff,
  WarningAmber,
  Block,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import HubSpotIcon from '@/components/icons/HubSpotIcon';
import adminApi, { HubSpotConnectorStatus } from '@/services/admin.api';
import GoogleSheetsConnector from './connectors/GoogleSheetsConnector';
// Shared with the Google Sheets card so both connectors render the same
// status shape, the same trigger chips and the same relative times.
import {
  StatusPill,
  TRIGGER_CHIP_SX,
  TRIGGER_LABEL,
  timeAgo,
  CONNECTOR_POLL_MS,
} from './connectors/connectorUi';

/** Plain-language mapping for what's otherwise a close-to-raw upstream
 *  error string — the industry norm (Zapier et al.) is to translate to a
 *  short, actionable sentence on the primary surface and never show the raw
 *  message there at all. */
function friendlyErrorMessage(raw: string | undefined, status: string): string | null {
  if (!raw) return null;
  if (status === 'reauth_required') return null; // that state already has its own banner + action
  const lower = raw.toLowerCase();
  if (lower.includes('rate limit') || lower.includes('429')) {
    return "HubSpot is asking us to slow down — this will retry automatically.";
  }
  if (lower.includes('timeout') || lower.includes('econnreset') || lower.includes('network')) {
    return "We couldn't reach HubSpot just now — this will retry automatically.";
  }
  return "Something went wrong on the last sync. Try syncing again — if it keeps happening, reconnect HubSpot.";
}

const Integrations: React.FC = () => {
  // Tenants can rename "Leads" (useTerminology) — the copy below must follow
  // whatever they call it, or the link would point at a nav item whose label
  // doesn't match the sentence describing it.
  const { terminology } = useTerminology();
  const location = useLocation();
  const [hubspotStatus, setHubspotStatus] = useState<HubSpotConnectorStatus | null>(null);
  const [hubspotLoading, setHubspotLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnectConfirmText, setDisconnectConfirmText] = useState('');
  const [callbackNotice, setCallbackNotice] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHubSpotStatus = useCallback(async () => {
    try {
      const status = await adminApi.getHubSpotConnectorStatus();
      setHubspotStatus(status);
    } catch (err) {
      console.error('Error fetching HubSpot connector status:', err);
    } finally {
      setHubspotLoading(false);
    }
  }, []);

  // Handle either connector's OAuth callback redirect (?hubspot=... /
  // ?googleSheets=...) and strip it from the URL so a page refresh doesn't
  // re-show the toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('hubspot');
    if (result === 'connected') {
      setCallbackNotice({
        severity: 'success',
        message: `HubSpot connected — importing your contacts as ${terminology.lead.plural.toLowerCase()} now.`,
      });
    } else if (result === 'error') {
      const reason = params.get('reason') || 'unknown_error';
      setCallbackNotice({
        severity: 'error',
        message: `Couldn't connect HubSpot (${reason.replace(/_/g, ' ')}). Please try again.`,
      });
    }

    // Google Sheets returns to this same page (?googleSheets=...). Its errors
    // get real explanations rather than a raw reason code: no_refresh_token in
    // particular is the one a tenant CAN act on, and "exchange failed" would
    // tell them nothing about what to do next.
    const sheets = params.get('googleSheets');
    if (sheets === 'connected') {
      setCallbackNotice({
        severity: 'success',
        message: 'Google connected. Add a sheet below to choose which tab to read.',
      });
    } else if (sheets === 'error') {
      const reason = params.get('reason') || 'unknown_error';
      const explained =
        reason === 'no_refresh_token'
          ? 'Google did not grant offline access. Remove Baatly from your Google account permissions, then connect again.'
          : reason === 'invalid_state'
            ? 'That connection link expired. Please start again.'
            : reason === 'account_mismatch'
              ? 'You signed in with a different Google account than the one already connected here. Nothing was changed. Your sheets can only be read by the account they were shared from — disconnect Google Sheets first if you really do want to switch accounts.'
              : `Couldn't connect Google Sheets (${reason.replace(/_/g, ' ')}). Please try again.`;
      setCallbackNotice({ severity: 'error', message: explained });
    }

    if (result || sheets) {
      params.delete('hubspot');
      params.delete('googleSheets');
      params.delete('reason');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
  }, []);

  useEffect(() => {
    fetchHubSpotStatus();
  }, [fetchHubSpotStatus]);

  // Live-poll only while a backfill is actually moving.
  useEffect(() => {
    const state = hubspotStatus?.connected ? hubspotStatus.backfill.state : undefined;
    const shouldPoll = state === 'scanning' || state === 'in_progress';

    if (shouldPoll && !pollRef.current) {
      pollRef.current = setInterval(fetchHubSpotStatus, CONNECTOR_POLL_MS);
    }
    if (!shouldPoll && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [hubspotStatus, fetchHubSpotStatus]);

  const handleConnectHubSpot = async () => {
    try {
      setConnecting(true);
      const url = await adminApi.getHubSpotAuthorizeUrl();
      window.location.href = url;
    } catch (err: any) {
      setConnecting(false);
      setCallbackNotice({
        severity: 'error',
        message: err?.response?.data?.error?.message || 'Failed to start HubSpot connection.',
      });
    }
  };

  const openDisconnectDialog = () => {
    setDisconnectConfirmText('');
    setDisconnectOpen(true);
  };

  const expectedConfirmText = hubspotStatus?.connected ? hubspotStatus.hubDomain || 'disconnect' : 'disconnect';

  const handleConfirmDisconnect = async () => {
    try {
      setDisconnecting(true);
      await adminApi.disconnectHubSpotConnector();
      setDisconnectOpen(false);
      setCallbackNotice({ severity: 'success', message: 'HubSpot disconnected.' });
      await fetchHubSpotStatus();
    } catch (err: any) {
      setCallbackNotice({
        severity: 'error',
        message: err?.response?.data?.error?.message || 'Failed to disconnect HubSpot.',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      await adminApi.triggerHubSpotSync();
      setCallbackNotice({ severity: 'success', message: 'Sync started — this can take a few minutes for large accounts.' });
    } catch (err) {
      console.error('Error triggering HubSpot sync:', err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Typography variant="h4" fontWeight="bold">
          Connections
        </Typography>
        <Button variant="outlined" onClick={fetchHubSpotStatus} disabled={hubspotLoading}>
          Refresh
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" mb={3}>
        Connect HubSpot or a Google Sheet to bring contacts into Baatly automatically as{' '}
        {terminology.lead.plural.toLowerCase()}.
      </Typography>

      {callbackNotice && (
        <Alert severity={callbackNotice.severity} sx={{ mb: 2 }} onClose={() => setCallbackNotice(null)}>
          {callbackNotice.message}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          {hubspotLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : !hubspotStatus?.connected ? (
            <>
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <HubSpotIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  HubSpot
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" mb={3} maxWidth={520}>
                Bring your HubSpot contacts into Baatly as{' '}
                {terminology.lead.plural.toLowerCase()} — a one-time import of everything you
                already have, then kept in sync automatically as HubSpot changes.
              </Typography>
              <Button
                variant="contained"
                disableElevation
                startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <HubSpotIcon htmlColor="#fff" />}
                onClick={handleConnectHubSpot}
                disabled={connecting}
                sx={{ bgcolor: '#FF7A59', '&:hover': { bgcolor: '#E66A4B' }, borderRadius: 1.5, px: 2.5 }}
              >
                {connecting ? 'Redirecting to HubSpot…' : 'Connect HubSpot'}
              </Button>
            </>
          ) : (
            <>
              {/* Header row — identity, quiet status, portal, actions */}
              <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                <HubSpotIcon sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  HubSpot
                </Typography>
                <StatusPill
                  status={hubspotStatus.status === 'disconnected' ? 'error' : hubspotStatus.status}
                />
                {hubspotStatus.hubDomain && (
                  <Typography variant="body2" color="text.secondary">
                    {hubspotStatus.hubDomain}
                  </Typography>
                )}
                <Box flex={1} />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <Sync />}
                  onClick={handleSyncNow}
                  disabled={syncing || hubspotStatus.status !== 'connected'}
                  sx={{ borderRadius: 1.5 }}
                >
                  Sync now
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<LinkOff />}
                  onClick={openDisconnectDialog}
                  sx={{ borderRadius: 1.5 }}
                >
                  Disconnect
                </Button>
              </Box>

              {hubspotStatus.status === 'reauth_required' && (
                <Alert severity="warning" sx={{ mt: 2 }} icon={<ErrorOutline />}>
                  HubSpot needs to be reconnected — a permission may have been revoked.{' '}
                  <Button size="small" onClick={handleConnectHubSpot} disabled={connecting}>
                    Reconnect
                  </Button>
                </Alert>
              )}
              {(() => {
                const friendly = friendlyErrorMessage(hubspotStatus.lastErrorMessage, hubspotStatus.status);
                return friendly ? (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {friendly}
                  </Alert>
                ) : null;
              })()}

              {/* Primary state — one line, plain language, the ONLY thing
                  shown by default (HubSpot's own native Data Sync card does
                  exactly this: "Up to date", nothing more, on the primary
                  surface). */}
              <Box mt={2.5}>
                {hubspotStatus.backfill.state === 'not_started' && (
                  <Typography variant="body2" color="text.secondary">
                    Waiting to start the first import…
                  </Typography>
                )}
                {(hubspotStatus.backfill.state === 'scanning' ||
                  hubspotStatus.backfill.state === 'in_progress') && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" mb={0.75}>
                      {hubspotStatus.backfill.state === 'scanning'
                        ? 'Scanning your HubSpot account…'
                        : `Importing HubSpot contacts as leads… ${hubspotStatus.backfill.processed}${
                            hubspotStatus.backfill.total ? ` of ~${hubspotStatus.backfill.total}` : ''
                          }`}
                    </Typography>
                    {hubspotStatus.backfill.total ? (
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          100,
                          (hubspotStatus.backfill.processed / hubspotStatus.backfill.total) * 100
                        )}
                        sx={{ borderRadius: 1, height: 6 }}
                      />
                    ) : (
                      <LinearProgress variant="indeterminate" sx={{ borderRadius: 1, height: 6 }} />
                    )}
                  </Box>
                )}
                {hubspotStatus.backfill.state === 'complete' && (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <CheckCircle sx={{ color: '#1B8A5A', fontSize: 20, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body2">
                        <strong>Up to date</strong> — {hubspotStatus.backfill.created} lead
                        {hubspotStatus.backfill.created === 1 ? '' : 's'} imported, last checked{' '}
                        {timeAgo(hubspotStatus.lastReconcileAt || hubspotStatus.backfill.finishedAt)}
                      </Typography>
                      {/* Names the DESTINATION explicitly. Your HubSpot contacts
                          become Baatly *leads* (they arrive from an external
                          source with provenance and a status pipeline — that is
                          what a Lead is here, whereas a Baatly Contact is
                          someone in a WhatsApp conversation). Saying only
                          "contacts imported" described the source and left the
                          destination unstated, sending people to look in the
                          wrong place. */}
                      <Typography variant="caption" color="text.secondary">
                        Your HubSpot contacts appear in Baatly as{' '}
                        {terminology.lead.plural.toLowerCase()}, tagged “hubspot”.{' '}
                        <MuiLink
                          component={RouterLink}
                          to={`${location.pathname.startsWith('/admin') ? '/admin' : ''}/activity/leads`}
                          sx={{ verticalAlign: 'baseline' }}
                        >
                          View {terminology.lead.plural.toLowerCase()}
                        </MuiLink>
                      </Typography>
                    </Box>
                  </Box>
                )}
                {hubspotStatus.backfill.state === 'failed' && (
                  <Alert
                    severity="error"
                    action={
                      <Button color="inherit" size="small" onClick={handleSyncNow} disabled={syncing}>
                        Retry
                      </Button>
                    }
                  >
                    The initial import hit an error.
                  </Alert>
                )}

                {!hubspotStatus.hasLeadsAccess && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    Leads object sync requires HubSpot Sales Hub Pro.
                  </Typography>
                )}
              </Box>

              {/* Progressive disclosure: the detailed run-by-run history is
                  one click away, never shown by default — matches the
                  researched pattern (Zapier's own history is a secondary
                  surface too, not the primary integration screen). */}
              <Button
                size="small"
                onClick={() => setHistoryOpen((v) => !v)}
                endIcon={historyOpen ? <ExpandLess /> : <ExpandMore />}
                sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
              >
                {historyOpen ? 'Hide sync details' : 'View sync details'}
              </Button>

              <Collapse in={historyOpen} timeout="auto" unmountOnExit>
                <Box mt={1.5} pt={2} borderTop="1px solid" borderColor="divider">
                  {/* Each stat renders only when it HAS a value. An empty
                      "Last live update — None yet" is pure noise: it takes up
                      a column to report the absence of something the tenant
                      never asked about, and on a fresh connection (before the
                      first webhook lands) it's the default state, so it reads
                      as a problem when nothing is wrong. */}
                  {(hubspotStatus.lastWebhookEventAt || hubspotStatus.lastReconcileAt) && (
                    <Box display="flex" gap={4} flexWrap="wrap" mb={2.5}>
                      {hubspotStatus.lastWebhookEventAt && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last live update
                          </Typography>
                          <Typography variant="body2">
                            {timeAgo(hubspotStatus.lastWebhookEventAt)}
                          </Typography>
                        </Box>
                      )}
                      {hubspotStatus.lastReconcileAt && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Last sync check
                          </Typography>
                          <Typography variant="body2">
                            {timeAgo(hubspotStatus.lastReconcileAt)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {hubspotStatus.recentRuns.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No sync activity recorded yet.
                    </Typography>
                  ) : (
                    <List dense disablePadding>
                      {hubspotStatus.recentRuns.slice(0, 5).map((run) => (
                        <ListItem
                          key={run._id}
                          disableGutters
                          sx={{
                            py: 1,
                            px: 1.25,
                            borderRadius: 1.5,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                  label={TRIGGER_LABEL[run.trigger] || run.trigger}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    border: 0,
                                    ...TRIGGER_CHIP_SX[run.trigger],
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {run.counts.created} new, {run.counts.updated} updated
                                  {run.counts.failed ? `, ${run.counts.failed} failed` : ''}
                                </Typography>
                                {run.status === 'failed' && (
                                  <Chip label="Failed" size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
                                )}
                              </Box>
                            }
                            secondary={
                              <Tooltip title={new Date(run.startedAt).toLocaleString()} placement="top" arrow>
                                <Typography variant="caption" color="text.secondary">
                                  {timeAgo(run.startedAt)}
                                </Typography>
                              </Tooltip>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </CardContent>
      </Card>

      {/* Disconnect confirmation — a deliberately heavier flow than a plain
          window.confirm(): lists real consequences and requires typing the
          connected portal's own domain to enable the destructive action,
          the same "type to confirm" pattern used for other irreversible
          actions (e.g. GitHub repo deletion). */}
      <GoogleSheetsConnector onNotice={setCallbackNotice} />

      <Dialog open={disconnectOpen} onClose={() => !disconnecting && setDisconnectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmber color="warning" />
          Disconnect HubSpot?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <List dense sx={{ mb: 1 }}>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Block fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={`Baatly stops importing new HubSpot contacts and stops receiving live HubSpot updates immediately.`}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText
                  primary={`${terminology.lead.plural} already imported into Baatly are not deleted or affected.`}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ErrorOutline fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText primary="This does not remove Baatly from your HubSpot account. To fully revoke access, also remove it from HubSpot under Settings → Connected Apps." />
              </ListItem>
            </List>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Type <strong>{expectedConfirmText}</strong> to confirm.
            </Typography>
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1.5 }}
            value={disconnectConfirmText}
            onChange={(e) => setDisconnectConfirmText(e.target.value)}
            placeholder={expectedConfirmText}
            disabled={disconnecting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDisconnectOpen(false)} disabled={disconnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDisconnect}
            disabled={disconnecting || disconnectConfirmText.trim() !== expectedConfirmText}
            startIcon={disconnecting ? <CircularProgress size={16} color="inherit" /> : <LinkOff />}
          >
            {disconnecting ? 'Disconnecting…' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
